import { Link } from 'react-router-dom'
import { Activity, Clock, ArrowRight } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuditLogs } from '@/hooks/use-audit-logs'
import { ACTION_LABELS, ENTITY_TYPE_LABELS } from '@/types/audit'
import type { AuditLogEntry } from '@/types/audit'

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minutes ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours} hours ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays} days ago`

  return date.toLocaleDateString()
}

/**
 * Single activity item component
 */
function ActivityItemRow({ entry }: { entry: AuditLogEntry }) {
  const actorDisplay = entry.actorName ?? entry.actorEmail ?? 'System'
  const actionLabel = ACTION_LABELS[entry.action] ?? entry.action
  const entityLabel = ENTITY_TYPE_LABELS[entry.entityType as keyof typeof ENTITY_TYPE_LABELS] ?? entry.entityType

  return (
    <div className="flex items-start gap-3 rounded-md p-3 hover:bg-muted/50 transition-colors">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <Activity className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 space-y-1 min-w-0">
        <p className="text-sm">
          <span className="font-medium">{actorDisplay}</span>{' '}
          <span className="text-muted-foreground">{actionLabel.toLowerCase()}</span>{' '}
          <span className="font-medium">{entityLabel}</span>
        </p>
        <p className="flex items-center text-xs text-muted-foreground">
          <Clock className="mr-1 h-3 w-3" />
          {formatRelativeTime(entry.createdAt)}
        </p>
      </div>
    </div>
  )
}

/**
 * Loading skeleton for activity items
 */
function ActivitySkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Empty state when no activity
 */
function EmptyActivity() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Activity className="mb-2 h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">No recent activity to display</p>
      <p className="text-xs text-muted-foreground">
        Start creating attributes or policies to see activity here
      </p>
    </div>
  )
}

/**
 * Recent Activity card for dashboard
 * Shows latest changes from the audit log
 */
export function RecentActivity() {
  const { data, isLoading } = useAuditLogs({
    pageSize: 5,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  })

  const entries = data?.data ?? []

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest changes in your environment</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/pac/audit-logs" className="gap-1">
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ActivitySkeleton />
        ) : entries.length === 0 ? (
          <EmptyActivity />
        ) : (
          <div className="space-y-1">
            {entries.map((entry) => (
              <ActivityItemRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
